import React, { useState, useEffect, useRef } from 'react';
import { searchHeroStaticNames } from '../../lib/heroSearchStaticNamesSearch';
import DateRangePicker from '../DateRangePicker/DateRangePicker';
import { useCurrency } from '../../context/CurrencyContext';
import { getCurrencyForNationality, NATIONALITY_OPTIONS } from '../../lib/currency';
import './HeroSearch.css';

/** Minimum typed characters before destination suggestions appear (focus alone does not open the list). */
const MIN_LOCATION_QUERY_LENGTH = 3;
const SUGGESTION_CACHE_MAX_ENTRIES = 120;

const buildSelectedDisplayLabel = (suggestion) => {
  if (!suggestion) return '';
  if (suggestion.type === 'place') {
    return (suggestion.name || '').trim();
  }
  const name = (suggestion.name || '').trim();
  const co = (suggestion.country || '').trim();
  return co ? `${name}, ${co}` : name;
};

const HeroSearch = ({ onSearch }) => {
  const { setSelectedCurrency } = useCurrency();
  const today = new Date().toISOString().split('T')[0];
  const defaultCheckIn = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const defaultCheckOut = new Date(Date.now() + 8 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  
  // Form state
  const [location, setLocation] = useState('');
  const [checkIn, setCheckIn] = useState(defaultCheckIn);
  const [checkOut, setCheckOut] = useState(defaultCheckOut);
  const [nights, setNights] = useState(1);
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [nationality, setNationality] = useState('GB');
  const [currency, setCurrency] = useState(() => getCurrencyForNationality('GB'));
  
  // Autocomplete state
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLocationFocused, setIsLocationFocused] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedCityId, setSelectedCityId] = useState('');
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [rooms, setRooms] = useState(1);
  const [showGuestSelector, setShowGuestSelector] = useState(false);
  const [staticLocationNames, setStaticLocationNames] = useState([]);

  const searchWrapperRef = useRef(null);
  const guestWrapperRef = useRef(null);
  const checkInInputRef = useRef(null);
  const checkOutInputRef = useRef(null);
  const locationInputRef = useRef(null);
  const [searching, setSearching] = useState(false);
  const suggestionCacheRef = useRef(new Map());

  useEffect(() => {
    let cancelled = false;
    import('../../data/heroSearchStaticNames.json')
      .then((mod) => {
        if (!cancelled) setStaticLocationNames(Array.isArray(mod.default) ? mod.default : []);
      })
      .catch((err) => {
        console.error('Failed to load static location names:', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    suggestionCacheRef.current.clear();
  }, [staticLocationNames]);

  useEffect(() => {
    const initialCurrency = getCurrencyForNationality(nationality);
    setCurrency(initialCurrency);
    setSelectedCurrency(initialCurrency);
  }, [nationality, setSelectedCurrency]);

  // Calculate nights when dates change
  useEffect(() => {
    if (checkIn && checkOut) {
      const checkInDate = new Date(checkIn);
      const checkOutDate = new Date(checkOut);
      const diffTime = Math.abs(checkOutDate - checkInDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setNights(diffDays || 1);
    }
  }, [checkIn, checkOut]);

  // Close dropdown and guest selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (guestWrapperRef.current && !guestWrapperRef.current.contains(event.target)) {
        setShowGuestSelector(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Suggestions from bundled static list only (no network).
  useEffect(() => {
    const searchLower = (location || '').toLowerCase().trim();

    if (!isLocationFocused) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    if (searchLower.length < MIN_LOCATION_QUERY_LENGTH) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    const cachedSuggestions = suggestionCacheRef.current.get(searchLower);
    if (cachedSuggestions) {
      setSuggestions(cachedSuggestions);
      setShowDropdown(cachedSuggestions.length > 0);
    }

    const handle = window.setTimeout(() => {
      const limited = searchHeroStaticNames(staticLocationNames, searchLower, {
        limit: 14,
        minLength: MIN_LOCATION_QUERY_LENGTH,
      });

      suggestionCacheRef.current.set(searchLower, limited);
      if (suggestionCacheRef.current.size > SUGGESTION_CACHE_MAX_ENTRIES) {
        const oldestKey = suggestionCacheRef.current.keys().next().value;
        if (oldestKey !== undefined) {
          suggestionCacheRef.current.delete(oldestKey);
        }
      }

      setSuggestions(limited);
      const showList = limited.length > 0;
      const showEmptyState = limited.length === 0 && staticLocationNames.length > 0;
      setShowDropdown(isLocationFocused && (showList || showEmptyState));
    }, 140);

    return () => window.clearTimeout(handle);
  }, [location, isLocationFocused, staticLocationNames]);

  const handleLocationSelect = (suggestion) => {
    const displayLabel = buildSelectedDisplayLabel(suggestion);
    setLocation(displayLabel);
    setSelectedLocation({ ...suggestion, displayLabel });
    if (suggestion.type === 'place') {
      setSelectedCityId('');
      setSelectedPropertyId('');
    } else if (suggestion.type === 'property') {
      setSelectedPropertyId(String(suggestion.id || ''));
      setSelectedCityId('');
    } else {
      setSelectedCityId(String(suggestion.id || ''));
      setSelectedPropertyId('');
    }
    setShowDropdown(false);
    setHighlightedIndex(-1);
  };

  const handleLocationChange = (e) => {
    const value = e.target.value;
    setLocation(value);
    setHighlightedIndex(-1);
    const trimmed = value.trim();
    if (!trimmed) {
      setSelectedCityId('');
      setSelectedPropertyId('');
      setSelectedLocation(null);
      return;
    }
    const prevLabel = (selectedLocation?.displayLabel || selectedLocation?.name || '').trim();
    if (prevLabel && trimmed.toLowerCase() !== prevLabel.toLowerCase()) {
      setSelectedCityId('');
      setSelectedPropertyId('');
      setSelectedLocation(null);
    }
  };

  // Keyboard navigation for suggestions
  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleLocationSelect(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    // Validate dates
    if (!checkIn || !checkOut) {
      alert('Please select check-in and check-out dates');
      return;
    }

    if (checkOut <= checkIn) {
      alert('Check-out date must be after check-in date');
      return;
    }

    // Validate guests
    if (adults < 1) {
      alert('At least 1 adult is required');
      return;
    }

    setSearching(true);

    try {
      let cityIdOut = selectedPropertyId ? '' : (selectedCityId || '').trim();
      let propertyIdOut = (selectedPropertyId || '').trim();

      let searchTarget = 'free';
      if (propertyIdOut) searchTarget = 'hotel';
      else if (cityIdOut) searchTarget = 'city';

      const navigationParams = {
        location: location.trim() || '',
        checkIn,
        checkOut,
        nights: nights,
        adults: adults,
        children: children,
        guests: adults + children,
        cityId: cityIdOut,
        propertyId: propertyIdOut,
        searchTarget,
        nationality: nationality,
        currency: currency,
      };

      console.log('🔍 Navigating to hotels with search params:', navigationParams);
      
      if (onSearch) {
        onSearch(navigationParams);
      }
    } catch (error) {
      console.error('❌ Search error:', error);
      alert(`Search failed: ${error.message || 'Please try again'}`);
    } finally {
      setSearching(false);
    }
  };

  const handleNationalityChange = (value) => {
    setNationality(value);
    const autoCurrency = getCurrencyForNationality(value);
    setCurrency(autoCurrency);
    setSelectedCurrency(autoCurrency);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]} ${date.getDate()}`;
  };

  const getDateRange = () => {
    if (!checkIn || !checkOut) return 'Check-in date — Check-out date';
    return `${formatDate(checkIn)} — ${formatDate(checkOut)}`;
  };

  const getGuestText = () => {
    return `${adults} ${adults === 1 ? 'adult' : 'adults'} · ${children} ${children === 1 ? 'child' : 'children'} · ${rooms} ${rooms === 1 ? 'room' : 'rooms'}`;
  };

  return (
    <section className="hero-search">
      <div className="hero-search__container">
        <div className="hero-search__content">
          <h1 className="hero-search__title">Find your next stay</h1>
          <p className="hero-search__subtitle">
            Search deals on hotels, homes, and much more...
          </p>
          
          <form className="hero-search__form" onSubmit={handleSearch}>
            <div className="hero-search__fields">
              <div
                className="hero-search__field hero-search__field--location"
                onClick={() => {
                  if (locationInputRef.current) {
                    locationInputRef.current.focus();
                  }
                }}
              >
                <div className="hero-search__field-icon">🛏️</div>
                <div className="hero-search__search-wrapper" ref={searchWrapperRef}>
                  <input
                    type="text"
                    className="hero-search__input"
                    ref={locationInputRef}
                    placeholder="Where are you going?"
                    value={location}
                    onChange={handleLocationChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => setIsLocationFocused(true)}
                    onBlur={() => {
                      // Delay hiding to allow click events on dropdown items
                      setTimeout(() => {
                        setIsLocationFocused(false);
                        setShowDropdown(false);
                      }, 200);
                    }}
                    disabled={searching}
                  />
                  {showDropdown && suggestions.length > 0 && (
                    <div className="hero-search__dropdown">
                      {suggestions.map((suggestion, index) => {
                        const isHighlighted = index === highlightedIndex;
                        return (
                          <div
                            key={`${suggestion.type}-${suggestion.name}-${index}`}
                            className={`hero-search__dropdown-item ${isHighlighted ? 'hero-search__dropdown-item--highlighted' : ''}`}
                            onClick={() => handleLocationSelect(suggestion)}
                            onMouseEnter={() => setHighlightedIndex(index)}
                          >
                            <div className="hero-search__dropdown-content">
                              <div className="hero-search__dropdown-row">
                                <span className="hero-search__dropdown-icon" aria-hidden="true">
                                  🔎
                                </span>
                                <div className="hero-search__dropdown-text">
                                  <span className="hero-search__dropdown-name">
                                    {suggestion.name}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {showDropdown &&
                    isLocationFocused &&
                    location.trim().length >= MIN_LOCATION_QUERY_LENGTH &&
                    suggestions.length === 0 &&
                    staticLocationNames.length > 0 && (
                    <div className="hero-search__dropdown">
                      <div className="hero-search__dropdown-item hero-search__dropdown-item--no-results">
                        No cities or hotels found matching "{location}"
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="hero-search__field hero-search__field--dates">
                <div className="hero-search__field-icon">📅</div>
                <DateRangePicker
                  checkIn={checkIn}
                  checkOut={checkOut}
                  onDatesChange={(newCheckIn, newCheckOut) => {
                    setCheckIn(newCheckIn);
                    setCheckOut(newCheckOut);
                    if (newCheckIn && newCheckOut) {
                      const checkInDate = new Date(newCheckIn);
                      const checkOutDate = new Date(newCheckOut);
                      const diffTime = Math.abs(checkOutDate - checkInDate);
                      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                      setNights(diffDays || 1);
                    }
                  }}
                  minDate={today}
                  />
              </div>
              
              <div className="hero-search__field hero-search__field--guests">
                <div className="hero-search__field-icon">👤</div>
                <div className="hero-search__guest-wrapper" ref={guestWrapperRef}>
                  <input
                    type="text"
                    className="hero-search__input"
                    value={getGuestText()}
                    readOnly
                    onClick={() => setShowGuestSelector(!showGuestSelector)}
                  />
                  {showGuestSelector && (
                    <div className="hero-search__guest-selector">
                      <div className="hero-search__guest-row">
                        <span>Adults</span>
                        <div className="hero-search__guest-controls">
                          <button
                            type="button"
                            onClick={() => setAdults(Math.max(1, adults - 1))}
                            disabled={adults <= 1}
                          >
                            −
                          </button>
                          <span>{adults}</span>
                          <button
                            type="button"
                            onClick={() => setAdults(adults + 1)}
                          >
                            +
                          </button>
                        </div>
                      </div>
                      <div className="hero-search__guest-row">
                        <span>Children</span>
                        <div className="hero-search__guest-controls">
                          <button
                            type="button"
                            onClick={() => setChildren(Math.max(0, children - 1))}
                            disabled={children <= 0}
                          >
                            −
                          </button>
                          <span>{children}</span>
                          <button
                            type="button"
                            onClick={() => setChildren(children + 1)}
                          >
                            +
                          </button>
                        </div>
              </div>
                      <div className="hero-search__guest-row">
                        <span>Rooms</span>
                        <div className="hero-search__guest-controls">
                          <button
                            type="button"
                            onClick={() => setRooms(Math.max(1, rooms - 1))}
                            disabled={rooms <= 1}
                  >
                            −
                          </button>
                          <span>{rooms}</span>
                          <button
                            type="button"
                            onClick={() => setRooms(rooms + 1)}
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="hero-search__field hero-search__field--nationality">
                <div className="hero-search__field-icon">🌍</div>
                <select
                  className="hero-search__select"
                  value={nationality}
                  onChange={(e) => handleNationalityChange(e.target.value)}
                  disabled={searching}
                  aria-label="Traveller nationality"
                >
                  {[...NATIONALITY_OPTIONS]
                    .sort((a, b) => a.label.localeCompare(b.label))
                    .map((opt) => (
                    <option key={opt.code} value={opt.code}>
                      {opt.label} ({opt.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
              <button 
                type="submit" 
                className="hero-search__submit-btn"
                disabled={searching || !checkIn || !checkOut || adults < 1}
              >
                {searching ? 'Searching...' : 'Search'}
              </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default HeroSearch;
